import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { patientsApi } from "@/api/endpoints";

/**
 * The standard patient identity block. Wherever we show a patient we surface the same five fields
 * by default — name, age, gender, UHID, phone — so staff always see a consistent identity header.
 * Use <PatientSummary> when you already hold the fields, or <PatientSummaryByUhid> to fetch them
 * from HealNTrack by UHID.
 */
export interface PatientSummaryData {
  name?: string | null;
  uhid?: string | null;
  gender?: string | null;
  dob?: string | null;   // ISO date; age is derived from it when `age` isn't given
  age?: number | null;
  phone?: string | null;
}

/** Whole-year age from an ISO date of birth; null if missing/unparseable. */
export function ageFromDob(dob?: string | null): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 200 ? age : null;
}

function Field({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <Stack spacing={0}>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontFamily: mono ? "monospace" : undefined }}>{value ?? "—"}</Typography>
    </Stack>
  );
}

/**
 * Presentational identity block. `extra` renders trailing chips/nodes (e.g. admission status)
 * next to the name; `dense` tightens spacing for dialogs.
 */
export function PatientSummary({
  data, extra, dense = false,
}: { data: PatientSummaryData; extra?: ReactNode; dense?: boolean }) {
  const age = data.age ?? ageFromDob(data.dob);
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: dense ? 0.5 : 1 }}>
        <Typography variant={dense ? "subtitle1" : "h6"} fontWeight={700}>
          {data.name?.trim() || "Unknown patient"}
        </Typography>
        {extra}
      </Stack>
      <Stack direction="row" spacing={dense ? 2.5 : 4} flexWrap="wrap" useFlexGap>
        <Field label="Age" value={age != null ? `${age} yrs` : "—"} />
        <Field label="Gender" value={data.gender || "—"} />
        <Field label="UHID" value={data.uhid || "—"} mono />
        <Field label="Phone" value={data.phone || "—"} />
      </Stack>
    </Box>
  );
}

/**
 * Fetches the patient by UHID from the stored Customer projection (real name/gender/DOB/phone) and
 * renders the standard block. Falls back to the given name/UHID while loading or if the patient
 * isn't found, so the identity is never blank.
 *
 * `patientId` (HealNTrack patientid) is the fallback key for queues whose rows have no UHID yet —
 * diagnostic orders only get one when they're processed, so without this their card renders blank.
 * UHID is preferred when both are present; it resolves from stored data alone.
 */
export function PatientSummaryByUhid({
  uhid, patientId, fallbackName, extra, dense = false,
}: {
  uhid?: string | null; patientId?: number | string | null;
  fallbackName?: string | null; extra?: ReactNode; dense?: boolean;
}) {
  const byUhid = !!uhid?.trim();
  const key = byUhid ? uhid!.trim() : patientId;
  const q = useQuery({
    queryKey: ["patient-summary", byUhid ? "uhid" : "patient", key],
    queryFn: () => byUhid
      ? patientsApi.summary(uhid!.trim())
      : patientsApi.summaryByPatientId(patientId!),
    enabled: byUhid || (patientId != null && patientId !== ""),
    staleTime: 60_000,
    retry: false,
  });

  if (q.isLoading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Loading patient…</Typography>
      </Stack>
    );
  }

  const p = q.data;
  return (
    <>
      <PatientSummary
        dense={dense}
        extra={extra}
        data={{
          name: p?.name ?? fallbackName,
          uhid: p?.uhid ?? uhid,
          gender: p?.gender,
          dob: p?.dob,
          phone: p?.phone,
        }}
      />
      {q.isError && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Patient not found in records — showing order details only.
        </Typography>
      )}
    </>
  );
}
